/* eslint-disable radix */
import { createSelector } from 'reselect';
import ultilitiesService from 'shared/util/ultilitiesService';
import { orderBy } from 'lodash';
import numeral from 'numeral';
import moment from 'moment';
import { PRODUCT_TYPE, PRODUCT_COMPONENT } from 'shared/constants';

export const getLanguage = state => state.language.locale;
export const getRTL = state => (state.language.locale !== 'EN');

/** User Profile */
export const getSummary = state => state.rootReducer.summary;

/** User Goals */
export const getGoals = state => state.goalSummaryResults;

/** Asset Allocations */
export const getAssetAllocations = state => state.assetAllocations;

/** get asset allocation donut chart details */
const getAssetAllocationSummary = createSelector(
  getAssetAllocations,
  (assetAllocations) => {
    const data = [];
    const allocation = assetAllocations.assetAllocation;
    if (allocation !== null) {
      allocation.forEach((alloc) => {
        data.push({
          assetClass: alloc.name,
          weight: alloc.weight,
          color: alloc.color,
        });
      });
    }
    return data;
  },
);

/** Portfolio Allocations */
const getPortfolioAllocation = createSelector(
  getAssetAllocations,
  (assetAllocations) => {
    const data = [];
    const allocation = assetAllocations.assetAllocation;
    if (allocation !== null) {
      allocation.forEach((alloc) => {
        alloc.assetAllocationDetails[0].fundDetails.forEach(item => {
          data.push({
            isinCode: item.isinCode,
            weight: `${numeral(item.weight).format('0.0')} %`,
          })
        })
      });
    }
    return data;
  }
)

/** get ISIN codes */
export const getIsinCodes = state => state.assetAllocations.isinCodes;
const getFunds = state => state.fundFactsheetReducer.funds;
// eslint-disable-next-line no-underscore-dangle
// const getUserId = state => state.rootReducer.userInfo._id;

/** get isin codes from fund factsheet results */
export const getIsinFromFundFactsheet = createSelector(
  getFunds,
  (funds) => {
    const data = [];
    if (!funds) return null;
    funds.forEach(fund => data.push(fund.isin));

    return data;
  },
);

/**
 * get product type based on isin code
 * @param {*} isin
 * @param {*} state
 */
export const getProductTypeByIsin = (isin, state) => {
  const funds = getFunds(state);
  const productType = funds.filter(fund => fund.isin === isin).map(fund => fund.productType);
  if (!productType) return null;
  return productType.toString();
};

// Get Key Facts based on Product Type
const getKeyFacts = (fund) => {
  if (!fund && !fund.productType) return null;
  let keyFacts = [];

  switch (fund.productType) {
    case PRODUCT_TYPE.ETF:
      keyFacts = [{
        title: 'products.netAssets',
        value: `${fund.netAssets ? `${ultilitiesService.formatUnitCurrency(fund.netAssets, fund.netAssetsCurrency)}` : '-'} `,
      }, {
        title: 'products.issuer',
        value: fund.issuer ? fund.issuer : '-',
      }, {
        title: 'products.expensesRatio',
        value: `${fund.expensesRatio ? `${fund.expensesRatio} %` : '-'}`,
      }, {
        title: 'products.fundInception',
        value: ultilitiesService.formatDate(fund.fundInterception),
      }];
      break;
    case PRODUCT_TYPE.MUTUAL_FUND:
      keyFacts = [{
        title: 'products.netAssets',
        value: `${fund.netAssets ? ultilitiesService.formatUnitCurrency(fund.netAssets, fund.netAssetsCurrency) : '-'}`,
      }, {
        title: 'products.subscriptionRedemption',
        value: fund.redemption ? fund.redemption : '-',
      }, {
        title: 'products.minInvestment',
        value: fund.minInitialInvestment ? fund.minInitialInvestment : '-',
      }, {
        title: 'products.fundInception',
        value: fund.fundInterception ? ultilitiesService.formatDate(fund.fundInterception) : '-',
      }];
      break;
    case PRODUCT_TYPE.MARKAZ_REAL_ESTATE:
      keyFacts = [{
        title: 'products.navUnit',
        value: `${(ultilitiesService.formatNavUnit(fund.lastClosingPrice) || ultilitiesService.formatNavUnit(fund.navUnit) || '')} ${fund.currency}`,
      }, {
        title: 'products.subscriptionRedemption',
        value: fund.redemption ? fund.redemption : '-',
      }, {
        title: 'products.managementFees',
        value: `${fund.expensesRatio ? `${fund.expensesRatio}` : '-'}`,
      }, {
        title: 'products.fundInception',
        value: fund.fundInterception ? ultilitiesService.formatDate(fund.fundInterception) : '-',
      }];
      break;
    default:
      keyFacts = null;
  }

  const keyFactsContent = {
    componentType: PRODUCT_COMPONENT.KEY_FACTS,
    title: 'product.keyFacts',
    data: keyFacts,
  };

  return keyFactsContent;
};

/**
 * Colors to use in sector allocations and portfolio compositions
 */
const arrayOfColors = ['#00599b', '#cbb565', '#82c3bd', '#e07b00', '#3FADEC', '#e48196', '#005a80', '#fbba00', '#94CFFF', '#bb1385', '#fd5e00'];

/**
 * Sector allocations
 * @param {*} data
 */
const getSectorAllocation = (data) => {
  const newData = [];
  if (data) data.forEach((x, i) => newData.push({ name: x.key, value: x.value ? parseFloat(x.value) : 0, color: arrayOfColors[i] }));
  let orderByHighestValueData = orderBy(newData, 'value', 'desc');

  if (orderByHighestValueData.length > 5) {
    const top7 = orderByHighestValueData.slice(0, 5);
    const sumOfTop7Value = top7.reduce((prev, cur) => prev + cur.value, 0);
    const othersPercentage = (100 - sumOfTop7Value).toFixed(2);
    top7.push({
      name: 'Others',
      value: Number(othersPercentage),
      color: '#A6CEE3',
    });
    orderByHighestValueData = top7;
  }
  return {
    componentType: PRODUCT_COMPONENT.PIE_CHART,
    title: 'products.sectorAllocations',
    data: orderByHighestValueData,
  };
};

// UPDATE THIS FUNCTION VALUE ONCE API HAVE THESE KEYS
const getPortfolioComponsition = (data) => {
  const newData = [];
  if (data) data.forEach((x, i) => newData.push({ name: x.name, value: Math.round(parseFloat(x.value) * 100), color: arrayOfColors[i] }));
  return {
    componentType: PRODUCT_COMPONENT.PIE_CHART,
    title: 'products.portfolioComposition',
    data: newData,
  };
};

// UPDATE THIS FUNCTION VALUE ONCE API HAVE THESE KEYS
const getPortfolioStatistics = (data) => {
  const { portfolioStatistic } = data;
  const newData = Object.keys(portfolioStatistic).map(key => ({
    title: key,
    value: (portfolioStatistic[key] % 1) !== 0 ? `${(Number(portfolioStatistic[key]) * 100).toFixed(2)} %` : portfolioStatistic[key],
    subTitle: '',
  }));
  const occupancyRatio = newData.find(x => x.title === 'occupancyRatio');
  const collectionRate = newData.find(x => x.title === 'collectionRate');
  occupancyRatio.subTitle = '12MonthsAverage';
  collectionRate.subTitle = '12MonthsAverage';

  return {
    componentType: PRODUCT_COMPONENT.PORTFOLIO_STATS,
    title: 'products.portfolioStatistics',
    dateUpdated: data.lastUpdateDate,
    data: newData,
  };
};

/**
 * get geographic allocations
 * @param {*} data
 * should be an array of data { key, Value}
 */
const getGeographicAllocation = (data) => {
  // key, Value

  const newData = [];
  if (data) data.forEach(x => newData.push({ name: x.key, value: parseFloat(x.value) }));
  let orderByHighestValueData = orderBy(newData, 'value', 'desc');

  if (orderByHighestValueData.length > 9) {
    const top10 = orderByHighestValueData.slice(0, 9);
    const sumOfTop10Value = top10.reduce((prev, cur) => prev + cur.value, 0);
    const othersPercentage = (100 - sumOfTop10Value).toFixed(2);
    top10.push({
      value: Number(othersPercentage),
      name: 'Others',
    });
    orderByHighestValueData = top10;
  }
  // orderByHighestValueData.push({ name: null, value: null });
  // console.log('geo data ', orderByHighestValueData);
  return {
    componentType: PRODUCT_COMPONENT.HORIZONTAL_BAR_CHART,
    title: 'products.geographicAllocations',
    data: orderByHighestValueData,
  };
};

const getTopHoldings = (selectedFunds) => {
  if (!selectedFunds) return [];

  const data = selectedFunds.slice(0, 7);
  const newData = [];

  if (selectedFunds) data.forEach(x => newData.push({ label: x.externalName, value: x.weighting ? `${(x.weighting).toFixed(2)} %` : '-' }));
  return {
    componentType: PRODUCT_COMPONENT.ITEM_LIST,
    // title: selectedFunds.productType === PRODUCT_TYPE.ETF ? 'products.top10Holdings' : 'products.top5Holdings',
    title: 'products.topHoldings',
    data: newData,
  };
};

/**
 * @todo Map the values based on PastPerformance component data format (need to make dynamic) or use directly same format as response
 * @param {pastperformance} selectedFunds
 */
const getPastPerformance = (selectedFunds) => {
  // Filter 3months, 6 months, 12 months, 3 years
  // const filteredPastPerformance = selectedFunds.pastPerformances ? selectedFunds.pastPerformances.filter(x => x.timePeriod === 'M3' || x.timePeriod === 'M6' || x.timePeriod === 'M12' || x.timePeriod === 'M36' || x.timePeriod === 'M60') : [];
  const filteredPastPerformance = selectedFunds.pastPerformances ? selectedFunds.pastPerformances.filter(x => x.timePeriod !== 'M0' && x.timePeriod !== 'M1' && x.timePeriod !== 'M255') : [];
  // console.log('past performances ', filteredPastPerformance);
  return {
    componentType: PRODUCT_COMPONENT.PAST_PERFORMANCES,
    title: 'products.pastPerformance',
    data: filteredPastPerformance,
  };
};

/**
 * @todo UNCOMMENT ONCE READY TO USE
 * @param {*} fundId
 * @param {*} state
 */
// const getFundPricePerformance = (fundPricePerformances, productType) => {
//   const title = () => {
//     let filteredTitle;
//     switch (productType) {
//       case PRODUCT_TYPE.ETF:
//         filteredTitle = 'products.growthOf10kUsd';
//         break;
//       case PRODUCT_TYPE.MARKAZ_REAL_ESTATE:
//         filteredTitle = 'products.growthOfFund';
//         break;
//       default:
//         filteredTitle = 'products.10YearsFundPricePerformance';
//         break;
//     }
//     return filteredTitle;
//   };
//   const newData = [];
//   const listKeys = [];
//   if (fundPricePerformances) {
//     fundPricePerformances.forEach((x) => {
//       newData.push({ name: ultilitiesService.formatDateToYears(x.Date), Fund: x.Value });
//     });
//   }

//   let keys = [];
//   if (newData) {
//     newData.forEach((x) => {
//       keys = [...new Set(Object.keys(x))];
//     });
//     keys.splice(0, 1);
//   }

//   if (keys) keys.forEach((key, i) => listKeys.push({ stroke: arrayOfColors[i], dataKey: key }));

//   return {
//     componentType: PRODUCT_COMPONENT.LINE_CHART,
//     title: title(),
//     data: newData,
//     listKeys,
//   };
// };

/**
 * get product details or fund factsheet details based on isin code
 * @param {*} isin
 * @param {*} state
 */
export const getProductDetailsById = (fundId, state) => {
  const funds = getFunds(state);
  const selectedFund = funds.find(fund => fund.isin === fundId);
  // console.log('selector filter ', selectedFund, selectedFund.sectorAllocations);

  // Components/contents
  const generalData = {
    fundId: selectedFund.fundId,
    fundType: selectedFund.fundType,
    productType: selectedFund.productType,
    isin: selectedFund.isin,
    logo: selectedFund.logo,
    banner: selectedFund.banner,
    title: selectedFund.fundName,
    reportingDate: selectedFund.reportingDate,
    description: selectedFund.fundDescription,
  };
  const keyFacts = getKeyFacts(selectedFund);
  const geographicAllocation = getGeographicAllocation(selectedFund.geographicAllocations);
  const sectorAllocations = getSectorAllocation(selectedFund.sectorAllocations);
  const topHoldings = getTopHoldings(selectedFund.topHoldings);
  const pastPerformance = getPastPerformance(selectedFund);
  // const fundPricePerformances = getFundPricePerformance(selectedFund.fundPricePerformances, selectedFund.productType);
  const portfolioComposition = getPortfolioComponsition(selectedFund.portfolioComposition); // UPDATE PARAM BASED ON API VALUE
  const portfolioStatistics = getPortfolioStatistics(selectedFund); // UPDATE PARAM BASED ON API VALUE

  // console.log('pastPerformance', pastPerformance);

  // Product details based on product type (Note: Change component order here)
  const EtfProductDetails = [keyFacts, geographicAllocation, sectorAllocations, topHoldings, pastPerformance]; // , fundPricePerformances
  const MutualFundProductDetails = [keyFacts, sectorAllocations, topHoldings, geographicAllocation, pastPerformance]; // , fundPricePerformances
  const MarkazRealEstateProductDetails = [keyFacts, portfolioStatistics, portfolioComposition, pastPerformance]; // , fundPricePerformances

  switch (selectedFund.productType) {
    case PRODUCT_TYPE.ETF:
      return Object.assign(generalData, { masterData: EtfProductDetails });
    case PRODUCT_TYPE.MUTUAL_FUND:
      return Object.assign(generalData, { masterData: MutualFundProductDetails });
    case PRODUCT_TYPE.MARKAZ_REAL_ESTATE:
      return Object.assign(generalData, { masterData: MarkazRealEstateProductDetails });
    default:
      return null;
  }
};

/** get Investment Products (a summary of products for simulation) */
const getInvestmentProducts = createSelector(
  getFunds,
  getPortfolioAllocation,
  (funds, portfolioAllocations) => {
    const data = [];
    if (!funds) return null;
    for (let i = 0; i < funds.length; i += 1) {
      const fund = funds[i];
      const findPortfolioAllocation = isin => portfolioAllocations && portfolioAllocations.find(item => item.isinCode === isin);
      data.push({
        name: fund.fundName,
        assetClass: fund.mainFocus,
        mainFocus: fund.mainFocus,
        navUnit: fund.lastClosingPrice ? `${ultilitiesService.formatNavUnit(fund.lastClosingPrice) || ultilitiesService.formatNavUnit(fund.navUnit)} ${fund.currency}` : '-',
        minInitialInvestment: fund.minInitialInvestment,
        threeYearPerformance: fund.threeYearPerformance ? `${numeral(fund.threeYearPerformance).format('0,0.00')} %` : '-',
        logo: fund.logo,
        etf: fund.etf,
        fundType: fund.fundType,
        netAssets: fund.netAssets ? `${ultilitiesService.formatUnitCurrency(fund.netAssets, fund.netAssetsCurrency)}` : '-',
        expensesRatio: fund.expensesRatio ? `${Number(fund.expensesRatio).toFixed(2)} %` : '-',
        index: i + 1,
        isinCode: fund.isin,
        portfolioAllocation: findPortfolioAllocation(fund.isin) && findPortfolioAllocation(fund.isin).weight,
      });
    }
    return data;
  },
);

const parseFinancialGoal = (goal) => {
  switch (goal) {
    case 'education':
      return 'Education';
    case 'retirement':
      return 'Retirement';
    case 'investment':
      return 'Investment';
    case 'big_purchase':
      return 'Big Purchase';
    case 'legacy':
      return 'Legacy';
    default:
      return 'Income';
  }
};

const parseRiskProfile = (risk) => {
  switch (risk) {
    case 'cautious':
      return 'Cautious';
    case 'moderately_cautious':
      return 'Moderately Cautious';
    case 'moderate':
      return 'Moderate';
    case 'moderately_adventurous':
      return 'Moderately Adventurous';
    default:
      return 'Adventurous';
  }
};

const parseRiskDescription = (description) => {
  switch (description) {
    case 'risk.cautious_description':
      return 'Suitable for investors who are seeking for secure investments with low risk to their principal and willing to accept lower return.';
    case 'risk.moderately_cautious_description':
      return 'Suitable for investors who are seeking for short to medium term investments with moderately low risk to their principal and willing to accept reasonable return.';
    case 'risk.moderate_description':
      return 'Suitable for investors who are seeking for medium to long term investments which will achieve better returns and willing to accept moderate risk to their principal.';
    case 'risk.moderately_adventurous_description':
      return 'Suitable for investors who are seeking for long term investments with an increased value for their investment and willing to accept moderately high risk to their principal.';
    case 'risk.adventurous_description':
      return 'Suitable for investors who are seeking for long term investments with the highest possible returns and willing to accept high risk to their principal.';
    default:
      return '';
  }
};

const parseInvestmenttype = (value) => {
  if (value === 'islamic') {
    return 'Islamic';
  }
  return 'Any Investment';
};

const filterGeography = (listOfGeography) => {
  let value = [];
  if (listOfGeography.includes('askGeographically.convo.quick_replies.2')) {
    value.push('Kuwait / GCC countries'); // kuwait_gcc - previouisly(Emerging markets) 
  }
  if (listOfGeography.includes('askGeographically.convo.quick_replies.3')) {
    value.push('Globally diversified'); // global - previously(Kuwait / GCC countries) 
  }
  if (listOfGeography.includes('askGeographically.convo.quick_replies.4')) {
    value.push('United States (USA)'); // developed_market - previously (Developed markets (incl. USA))
  }
  if (listOfGeography.includes('askGeographically.convo.quick_replies.5')) {
    value.push('Gold as an investment'); 
  }
  // if (listOfGeography.includes('askGeographically.convo.quick_replies.1')) {
  //   value = ['All'];
  // }
  return value.join(', ');
};

const parseGeographyToId = (listOfGeography) => {
  let value = [];
  if (listOfGeography.includes('askGeographically.convo.quick_replies.2')) {
    value.push('kuwait_gcc'); 
  }
  if (listOfGeography.includes('askGeographically.convo.quick_replies.3')) {
    value.push('global'); 
  }
  if (listOfGeography.includes('askGeographically.convo.quick_replies.4')) {
    value.push('developed_market'); 
  }
  if (listOfGeography.includes('askGeographically.convo.quick_replies.5')) {
    value.push('gold_investment'); 
  }
  // if (listOfGeography.includes('askGeographically.convo.quick_replies.1')) {
  //   value = ['All'];
  // }
  return value.join(', ');
};

const parseLocation = (location) => {
  if (location === 'another_place') return 'Another place';
  return 'Markaz office';
};

const getCtaType = createSelector(
  getSummary,
  (summary) => {
    const type = summary['summary.proceedOption'];
    switch (type) {
      case 'email_product':
        return 'simulation';
      case 'open_account':
        return 'open_account';
      case 'meet_advisor':
        return 'appointment';
      default:
        return null;
    }
  },
);

/** Get Simulation Data */
export const getSimulationData = createSelector(
  [
    getSummary,
    getGoals,
    getAssetAllocationSummary,
    getInvestmentProducts,
    getCtaType,
    // getUserId,
  ],
  (
    summary,
    goalSummary,
    assetAllocation,
    products,
    type,
    // userId,
  ) => {
    const appointment = {
      appointmentDate: summary['summary.preferredDate'] ? moment(summary['summary.preferredDate'], 'D MMMM YYYY').locale('en').format('Do MMMM YYYY') : null,
      appointmentTime: summary['summary.preferredTime'] ? summary['summary.preferredTime'] : null,
      appointmentLocation: summary['summary.preferredLocation'] ? parseLocation(summary['summary.preferredLocation']) : null,
      advisorId: summary['summary.managerID'] ? summary['summary.managerID'] : null,
      advisorName: summary['summary.advisorName'] ? summary['summary.advisorName'] : null,
      advisorTitle: summary['summary.advisorTitle'] ? summary['summary.advisorTitle'] : null,
    };

    const initialInvestment = numeral(summary['summary.initialInvestment']).value();
    const isRetail = initialInvestment >= 5000 && initialInvestment <= 99999;
    const isAffluent = initialInvestment >= 100000 && initialInvestment <= 250000;

    const paQuery = {
      geographyReference: parseGeographyToId(summary['summary.geographicalInterest']),
      clientSegment: isRetail ? 'retail' : isAffluent ? 'affluent' : 'private',
      strategyRiskLevel: summary['summary.risk'],
      investmentType: summary['summary.investmentPreference'] === 'islamic' ? 2 : 1,
    }

    const clientData = {
      channel: 'markaz',
      host: window.location.origin,
      clientName: summary['summary.name'],
      riskProfile: parseRiskProfile(summary['summary.risk']),
      riskProfileDescription: parseRiskDescription(summary['summary.risk_description']),
      date: moment().locale('en').format('MMMM DD, YYYY'),
      financialGoal: parseFinancialGoal(summary['summary.goal']),
      initialInvestment: summary['summary.initialInvestment'],
      duration: ultilitiesService.yearBuilder(summary['summary.targetCompletion'], { singular: 'year', plural: 'years' }),
      age: parseInt(summary['summary.age']),
      geographical: filterGeography(summary['summary.geographicalInterest']),
      investmentType: parseInvestmenttype(summary['summary.investmentPreference']),
      targetAmount: summary['summary.targetAmount'],
      annualInvestment: summary['summary.annualInvestment'],
      assetAllocation,
      products,
      investmentGoal: goalSummary,
      paQuery,
    };

    // const clientDataWithGoal = Object.assign(clientData, goalSummary);
    const generalData = {
      clientName: summary['summary.name'],
      clientEmail: (summary['summary.email'] === 'Skip' || summary['summary.email'] === 'تخطي') ? '' : summary['summary.email'],
      type: type || 'open_account', // if no type saved, use open_account (default)
    };

    switch (type) {
      case 'appointment':
        const newClientData = Object.assign(clientData, appointment);
        return Object.assign(generalData, { clientData: newClientData });
      default:
        return Object.assign(generalData, { clientData });
    }
  }
  ,
);
